<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import StarsRateSvg from './stars-rate-svg.vue';
import TitleWithLinesBlock from './title-with-lines-block.vue';

const b = bem('relive-memories-block');

export default {
  mixins: [publishGuardMixin],
  name: 'ReliveMemoriesBlock',
  components: {
    PencilIcon,
    TitleWithLinesBlock,
    EditTitleWithLinesModal,
    EditButtonModal,
    StarsRateSvg,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      titleModalVisible: false,
      leftButtonModalVisible: false,
      rightButtonModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    isDarkTheme() {
      return this.data?.isDarkTheme ?? true;
    },
    withoutRate() {
      return this.data?.withoutRate ?? true;
    },
    withMobileLine() {
      return this.data?.withMobileLine ?? true;
    },
    titleList() {
      return this.data?.titleList || [];
    },
    // Buttons data
    leftButtonText() {
      return this.data?.leftButtonText || 'Place An Order Online';
    },
    leftButtonTextMobile() {
      return this.data?.leftButtonTextMobile || this.leftButtonText;
    },
    leftButtonLink() {
      return this.data?.leftButtonLink || '';
    },
    rightButtonText() {
      return this.data?.rightButtonText || 'Talk To Our Team';
    },
    rightButtonTextMobile() {
      return this.data?.rightButtonTextMobile || this.rightButtonText;
    },
    rightButtonLink() {
      return this.data?.rightButtonLink || '';
    },
    displayLeftButtonText() {
      return this.isMobileView ? this.leftButtonTextMobile : this.leftButtonText;
    },
    displayRightButtonText() {
      return this.isMobileView ? this.rightButtonTextMobile : this.rightButtonText;
    },
    reviewsCount() {
      return this.data?.reviewsCount || 2717;
    },
  },
  methods: {
    // Title modal
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    handleTitleSave(values) {
      this.$emit('update', { titleList: values });
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update', { titleList: [] });
      this.titleModalVisible = false;
    },
    // Left button modal
    openLeftButtonModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.leftButtonModalVisible = true;
      }
    },
    handleLeftButtonSave(values) {
      this.$emit('update', {
        leftButtonText: values.buttonText,
        leftButtonTextMobile: values.buttonTextMobile,
        leftButtonLink: values.buttonLink,
      });
      this.leftButtonModalVisible = false;
    },
    // Right button modal
    openRightButtonModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.rightButtonModalVisible = true;
      }
    },
    handleRightButtonSave(values) {
      this.$emit('update', {
        rightButtonText: values.buttonText,
        rightButtonTextMobile: values.buttonTextMobile,
        rightButtonLink: values.buttonLink,
      });
      this.rightButtonModalVisible = false;
    },
    // Toggle handlers
    toggleDarkTheme() {
      this.$emit('update', { isDarkTheme: !this.isDarkTheme });
    },
    toggleRate() {
      this.$emit('update', { withoutRate: !this.withoutRate });
    },
    toggleMobileLine() {
      this.$emit('update', { withMobileLine: !this.withMobileLine });
    },
  },
};
</script>

<template>
  <article :class="b('', { mix: className, dark: isDarkTheme, isMobileView })">
    <!-- Title with lines -->
    <div
      :class="b('title-wrap', { editable: isEditMode, isMobileView })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openTitleModal"
      @keyup.enter="openTitleModal"
    >
      <TitleWithLinesBlock
        v-if="titleList.length"
        :class="b('title', { isMobileView })"
        :is-mobile-view="isMobileView"
        :text-list="titleList"
      />
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add title
      </div>
      <PencilIcon
        v-if="isEditMode && titleList.length"
        :class="b('edit-icon')"
        :color="isDarkTheme ? '#fff' : '#293241'"
        :size="20"
      />
    </div>

    <!-- Mobile line (optional) -->
    <div
      v-if="withMobileLine && isMobileView"
      :class="b('mobile-line')"
    />

    <!-- Buttons block -->
    <div :class="b('buttons-block', { isMobileView })">
      <!-- Left button (golden) -->
      <div
        :class="b('button-wrap', { left: true, isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openLeftButtonModal"
        @keyup.enter="openLeftButtonModal"
      >
        <button
          type="button"
          :class="b('button', { golden: true, left: isMobileView, isMobileView })"
        >
          {{ displayLeftButtonText }}
        </button>
        <PencilIcon
          v-if="isEditMode"
          :class="b('button-edit-icon')"
          color="#293241"
          :size="16"
        />
      </div>

      <!-- Right button (horizon/blue) -->
      <div
        :class="b('button-wrap', { right: true, isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openRightButtonModal"
        @keyup.enter="openRightButtonModal"
      >
        <button
          type="button"
          :class="b('button', { horizon: true, right: isMobileView, isMobileView })"
        >
          {{ displayRightButtonText }}
        </button>
        <PencilIcon
          v-if="isEditMode"
          :class="b('button-edit-icon', { light: true })"
          color="#fff"
          :size="16"
        />
      </div>
    </div>

    <!-- Rate line (optional) -->
    <div v-if="!withoutRate" :class="b('rate', { isMobileView, dark: isDarkTheme })">
      <div :class="b('rate-left')">
        <StarsRateSvg
          :class="b('stars')"
          :width="isMobileView ? 142 : 142"
          :height="isMobileView ? 24 : 24"
          fill="#eec586"
          stroke="#eec586"
        />
        <span :class="b('rate-count', { dark: isDarkTheme })">
          ({{ reviewsCount }})
        </span>
      </div>
      <span
        :class="b('rate-text', { dark: isDarkTheme, isMobileView })"
      >
        America's Choice Since 2007
      </span>
      <span :class="b('rate-link')">See All Reviews</span>
    </div>

    <!-- Edit mode toggles -->
    <div v-if="isEditMode" :class="b('toggle-controls')">
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label', { dark: isDarkTheme })">Show Rate:</span>
        <a-switch
          :checked="!withoutRate"
          @change="toggleRate"
        />
      </div>
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label', { dark: isDarkTheme })">Mobile Line:</span>
        <a-switch
          :checked="withMobileLine"
          @change="toggleMobileLine"
        />
      </div>
    </div>

    <!-- Modals -->
    <EditTitleWithLinesModal
      :visible="titleModalVisible"
      :data="titleList"
      :show-delete="titleList.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <EditButtonModal
      :visible="leftButtonModalVisible"
      :button-text="leftButtonText"
      :button-text-mobile="leftButtonTextMobile"
      :button-link="leftButtonLink"
      modal-title="Edit Left Button"
      @close="leftButtonModalVisible = false"
      @save="handleLeftButtonSave"
    />

    <EditButtonModal
      :visible="rightButtonModalVisible"
      :button-text="rightButtonText"
      :button-text-mobile="rightButtonTextMobile"
      :button-link="rightButtonLink"
      modal-title="Edit Right Button"
      @close="rightButtonModalVisible = false"
      @save="handleRightButtonSave"
    />
  </article>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.relive-memories-block {
  & * {
    font-family: Montserrat, sans-serif;
  }

  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px 0;
  background-color: #d7e8f0;
  gap: 32px;

  @include mediaMinMax(1025, $normal) {
    padding: vw-full(64) 0;
    gap: vw-full(32);
  }

  &_isMobileView {
    padding: 40px 0;
    gap: 20px;
  }

  &_dark {
    background-color: #293241;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 8px 16px;
    border-radius: 8px;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      padding: vw-full(8) vw-full(16);
      border-radius: vw-full(8);
    }

    &_isMobileView {
      width: 100%;
    }

    &_editable {
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.1);

        .relive-memories-block__edit-icon {
          opacity: 1;
        }
      }
    }
  }

  &__title {
    &_isMobileView {
      white-space: pre-wrap;
      text-transform: uppercase !important;
    }
  }

  &__add-field {
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 16px 32px;
    border: 2px dashed rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
      padding: vw-full(16) vw-full(32);
      border-radius: vw-full(8);
    }

    &:hover {
      border-color: rgba(255, 255, 255, 0.5);
      color: rgba(255, 255, 255, 0.8);
    }
  }

  &__edit-icon {
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &__mobile-line {
    width: 104px;
    height: 1px;
    background: #fff;
  }

  &__buttons-block {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 32px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(32);
    }

    &_isMobileView {
      flex-direction: column;
      gap: 28px;
      width: 100%;
    }
  }

  &__button-wrap {
    position: relative;
    display: flex;
    align-items: center;

    &__button {
      &:hover {
        filter: brightness(0.95);
      }
    }

    &__button-edit-icon {
      &:hover {
        opacity: 1;
      }
    }

    &_left#{&}_isMobileView {
      margin-right: auto;
      margin-left: 0;
    }

    &_right#{&}_isMobileView {
      margin-left: auto;
      margin-right: 0;
    }
  }

  &__button {
    width: 480px;
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    outline: none;
    cursor: pointer;
    border-radius: 20px 3px;
    box-shadow: 4px 4px 8px 0 rgba(95, 138, 164, 0.2);
    font-family: Montserrat, sans-serif;
    font-size: 22px;
    font-weight: 600;
    text-transform: uppercase;
    transition: filter 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(480);
      height: vw-full(64);
      font-size: vw-full(22);
      border-radius: vw-full(20) vw-full(3);
    }

    &_golden {
      background-color: #ecc486;
      color: #293241;
    }

    &_horizon {
      background-color: #5f8aa4;
      color: #fff;
    }

    // Mobile styles
    &_isMobileView {
      width: 374px;
      height: 52px;
      font-size: 18px;
      font-weight: 500;
      border-radius: 26px;
    }

    &_left#{&}_isMobileView {
      border-radius: 0 26px 26px 0;
      justify-content: flex-end;
      padding-right: 50px;
    }

    &_right#{&}_isMobileView {
      border-radius: 26px 0 0 26px;
      justify-content: flex-start;
      padding-left: 70px;
    }
  }

  &__button-edit-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: opacity 0.2s ease;
    right: 16px;

    @include mediaMinMax(1025, $normal) {
      right: vw-full(16);
    }
  }

  &__button-wrap_left &__button-edit-icon {
    right: auto;
    left: 16px;

    @include mediaMinMax(1025, $normal) {
      left: vw-full(16);
    }
  }

  &__button-wrap_left#{&}__button-wrap_isMobileView &__button-edit-icon {
    left: auto;
    right: 70px;
  }

  &__button-wrap_right#{&}__button-wrap_isMobileView &__button-edit-icon {
    right: auto;
    left: 20px;
  }

  &__rate {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 32px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      margin-top: vw-full(32);
    }

    &_isMobileView {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-top: 20px;
    }
  }

  &__rate-left {
    display: flex;
    align-items: center;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__stars {
    width: 142px;
    height: 24px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(142);
      height: vw-full(24);
    }
  }

  &__rate-count {
    color: #293241;
    font-size: 18px;
    font-weight: 400;
    font-family: Montserrat, sans-serif;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
    }

    &_dark {
      color: #fff;
    }
  }

  &__rate-text {
    color: #293241;
    font-size: 18px;
    font-weight: 400;
    font-family: Montserrat, sans-serif;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
    }

    &_dark {
      color: #fff;
    }

    &_isMobileView {
      order: -1;
      text-transform: uppercase;
    }
  }

  &__rate-link {
    color: #ecc486;
    font-size: 18px;
    font-weight: 500;
    text-transform: uppercase;
    text-decoration: underline;
    cursor: pointer;
    font-family: Montserrat, sans-serif;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
    }
  }

  // Toggle controls (edit mode)
  &__toggle-controls {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-top: 24px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(24);
      margin-top: vw-full(24);
      padding: vw-full(16);
      border-radius: vw-full(8);
    }
  }

  &__toggle-item {
    display: flex;
    align-items: center;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__toggle-label {
    font-size: 14px;
    font-weight: 500;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
    }

    &_dark {
      color: #fff;
    }
  }
}
</style>
